
import React, { useState, useRef, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Check, FileText, Trash, Copy, Edit, Download, Upload, Printer, Archive, ChevronDown, FileDown, SlidersHorizontal, Tag, Loader2 } from 'lucide-react';
import { useShortcut, DocumentShortcut } from '../context/ShortcutContext';
import { useTranslation } from 'react-i18next';
import GenericMasterHeaderCompactActions from './GenericMasterHeaderCompactActions';

interface GenericMasterHeaderProps {
    // Entity display
    entityLabel: string; // "Partner", "Currency", "1000", etc.
    entityCode?: string; // The current entity's code
    entityName?: string; // The current entity's name
    entityId?: string; // The current entity's ID (for shortcuts)

    // Shortcut configuration
    shortcutCategory?: string; // 'partners', 'currencies', 'material', etc.
    shortcutSubCategory?: string;
    shortcutSubType?: string;
    documentTypeId?: string; // For document shortcuts

    // State
    isCreating?: boolean;
    isEditing?: boolean;
    isSaving?: boolean;
    
    hasChanges?: boolean;
    isFormValid?: boolean; // Whether the form passes validation (required fields filled)
    hasEntity?: boolean; // Whether an entity is currently loaded

    // Actions
    onSave?: () => void;
    onNew?: () => void;
    onEdit?: () => void;
    onDelete?: () => void;
    deleteDisabled?: boolean; // Extra condition to disable the Delete button (e.g. active record)
    deleteDisabledTitle?: string; // Title shown when deleteDisabled is true
    newDisabled?: boolean; // Extra condition to disable the New button (e.g. a record already exists for the current period)
    newDisabledTitle?: string; // Title shown when newDisabled is true
    newTitle?: string; // Tooltip of the enabled New button (default: "New <entityLabel>")
    deleteTitle?: string; // Tooltip of the enabled Delete button (default: "Delete <entityLabel>")
    saveDisabled?: boolean; // Extra condition to disable the Save button (e.g. a locked record)
    saveDisabledTitle?: string; // Title shown when saveDisabled is true
    onCopy?: () => void;
    copyTitle?: string; // Tooltip of the enabled Copy button (default: "Copy <entityLabel>")
    onExcelDownload?: () => void;
    onExcelUpload?: () => void;
    excelUploadDisabled?: boolean;
    excelUploadDisabledTitle?: string;
    onEntityLabelClick?: () => void;

    // Search component (render prop)
    renderSearch?: () => React.ReactNode;

    // Custom actions injected to the left of the standard action buttons
    renderActionsLeft?: () => React.ReactNode;

    // Excel export component (render prop) - replaces onExcelDownload when provided
    renderExcelExport?: () => React.ReactNode;

    // PDF print
    onPrintPdf?: () => void;
    onDirectPrint?: () => void;
    onPrintLabels?: () => void;

    // Print forms dropdown (render prop) - replaces print actions when provided
    renderPrintDropdown?: () => React.ReactNode;

    // Archive
    onArchive?: () => void;

    // Optional children for custom content (e.g., form fields)
    children?: React.ReactNode;
    belowHeader?: React.ReactNode;

    // Layout options
    showActions?: boolean; // Whether to show action buttons (default: true if any action is provided)
    leftWidth?: string; // Width of left section (default: '60%')
    rightWidth?: string; // Width of right section (default: '40%')
    dense?: boolean; // Tighter vertical padding and smaller controls (default: true)
    // Opt-in: just the action row (no card chrome, no left block), controls h-8 like the planning toolbar buttons.
    toolbarRow?: boolean;
    variant?: 'default' | 'compact';
}

const GenericMasterHeader: React.FC<GenericMasterHeaderProps> = ({
    // Entity display
    entityLabel,
    entityCode,
    entityName,
    entityId,

    // Shortcut configuration
    shortcutCategory,
    shortcutSubCategory,
    shortcutSubType,
    documentTypeId,

    // State
    isCreating = false,
    isEditing = false,
    isSaving = false,
    hasChanges = true,
    isFormValid = true, // Default to true for backwards compatibility
    hasEntity = false,

    // Actions
    onSave,
    onNew,
    onEdit,
    onDelete,
    deleteDisabled = false,
    deleteDisabledTitle,
    newDisabled = false,
    newDisabledTitle,
    newTitle,
    deleteTitle,
    saveDisabled = false,
    saveDisabledTitle,
    onCopy,
    copyTitle,
    onExcelDownload,
    onExcelUpload,
    excelUploadDisabled = false,
    excelUploadDisabledTitle,
    onEntityLabelClick,

    // Search
    renderSearch,

    // Custom left actions
    renderActionsLeft,

    // Excel export render prop
    renderExcelExport,

    // PDF print
    onPrintPdf,
    onDirectPrint,
    onPrintLabels,

    // Print forms dropdown
    renderPrintDropdown,

    // Archive
    onArchive,

    // Children
    children,
    belowHeader,

    // Layout
    showActions,
    leftWidth = '60%',
    rightWidth = '40%',
    dense = true,
    toolbarRow = false,
    variant = 'default',
}) => {
    const { addShortcut, shortcuts, hasShortcut } = useShortcut();
    const location = useLocation();
    const navigate = useNavigate();
    const { t } = useTranslation();

    // Determine if we should show the action buttons section
    const hasAnyAction = onSave || onNew || onEdit || onDelete || onArchive || onCopy || onExcelDownload || onExcelUpload || renderSearch || renderExcelExport || onPrintPdf || onDirectPrint || onPrintLabels || renderPrintDropdown;
    const shouldShowActions = showActions !== undefined ? showActions : hasAnyAction;
    const actionIconSize = dense ? 14 : 20;
    const actionButtonClass = toolbarRow ? 'p-1 h-8' : dense ? 'p-1 h-7' : 'p-1.5 h-9';
    const hasSearch = !!renderSearch;
    const searchHighlightRef = useRef<HTMLDivElement>(null);

    // Autofocus the header search when the screen is entered. The visible cue is the
    // focus-within ring on the wrapper below, which stays for as long as the field
    // really holds focus — a timed flash looked identical whether focus landed or not.
    useEffect(() => {
        if (!hasSearch) return;
        const focusTimer = window.setTimeout(() => {
            const input = searchHighlightRef.current?.querySelector('input');
            input?.focus({ preventScroll: true });
            input?.select();
        }, 0);
        return () => window.clearTimeout(focusTimer);
    }, [location.key, location.pathname, location.search, hasSearch]);

    const handleAddShortcut = () => {
        const currentPath = location.pathname;
        
        // Check if shortcut already exists for this exact path
        const existingShortcut = shortcuts.find(s => s.path === currentPath);

        if (existingShortcut) {
            // Shortcut already exists - do nothing (we're already on this page)
            return;
        }
        
        // Add new shortcut with the current page's info
        addShortcut({
            documentId: entityId,
            documentTypeId: documentTypeId,
            documentNumber: entityCode || '',
            documentCode: entityLabel,
            documentName: entityName || entityLabel,
            category: shortcutCategory || 'general',
            subCategory: shortcutSubCategory,
            subType: shortcutSubType,
            path: currentPath
        });
    };

    return (
        <>
        {isSaving && (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 backdrop-blur-sm">
                <div className="flex items-center gap-3 bg-white px-6 py-4 rounded-xl shadow-lg">
                    <Loader2 size={24} className="animate-spin text-blue-600" />
                    <span className="text-sm font-medium text-gray-700">{t('buttons.saving', 'Saving...')}</span>
                </div>
            </div>
        )}
        <div className={toolbarRow ? 'w-full' : `w-full bg-white border-b border-gray-200 ${dense ? 'py-1 px-2' : 'py-4 px-4'} shadow-sm mt-1 mb-0`}>
            <div
                className={children ? "grid items-center w-full gap-3" : "flex items-center w-full gap-3"}
                style={children ? { gridTemplateColumns: 'auto 1fr auto' } : undefined}
            >
                {/* Left side: Entity label, code, name */}
                {!toolbarRow && <div
                    className="flex items-center space-x-4 min-w-0 flex-shrink-0"
                    style={{ width: children ? undefined : (shouldShowActions ? leftWidth : '100%') }}
                >
                    {/* Gradient button with entity label - hide for "Header" */}
                    {entityLabel !== "Header" && (
                        <button
                            onClick={onEntityLabelClick || handleAddShortcut}
                            className={`text-white ${dense ? 'px-2 py-1 rounded-md' : 'px-4 py-2 rounded-lg'} shadow-lg transition-all duration-200 flex-shrink-0 hover:shadow-xl cursor-pointer hover:opacity-90`}
                            style={{ background: `linear-gradient(to right, var(--color-primary), var(--color-secondary))` }}
                            title={onEntityLabelClick ? `New ${entityLabel}` : "Click to add shortcut"}
                        >
                            <span className={`${dense ? 'text-xs' : 'text-lg'} font-bold`}>{entityLabel}</span>
                        </button>
                    )}

                    {/* Entity code and name */}
                    {(entityCode || entityName) && (
                        <div className="flex-shrink-0">
                            <h1 className={`${dense ? 'text-sm' : 'text-xl'} font-bold text-gray-800 leading-tight`}>
                                {entityCode || entityName}
                            </h1>
                            <div className={`${dense ? 'h-0.5 mt-0.5' : 'h-1 mt-1'} rounded-full`} style={{ background: `linear-gradient(to right, var(--color-primary), var(--color-secondary))` }}></div>
                        </div>
                    )}
                </div>}

                {/* Middle: children fill the 1fr grid column when children provided */}
                {children && (
                    <div className="flex items-center min-w-0">
                        {children}
                    </div>
                )}

                {/* Right side: Actions and Search */}
                {shouldShowActions && (
                    <div className="flex items-center justify-end min-w-0 ml-auto flex-shrink-0" style={{ width: children || toolbarRow ? undefined : rightWidth }}>
                        {variant === 'compact' ? (
                            <GenericMasterHeaderCompactActions
                                searchRef={searchHighlightRef}
                                renderSearch={renderSearch}
                                onNew={onNew}
                                onSave={onSave}
                                onDelete={onDelete}
                                newDisabled={isCreating || isSaving || newDisabled}
                                saveDisabled={isSaving || !hasChanges || !isFormValid || saveDisabled}
                                deleteDisabled={!hasEntity || isCreating || isSaving || deleteDisabled}
                                newTitle={newDisabled
                                    ? (newDisabledTitle || `Cannot create ${entityLabel}`)
                                    : (newTitle || `New ${entityLabel}`)}
                                saveTitle={saveDisabled
                                    ? (saveDisabledTitle || `Cannot save ${entityLabel}`)
                                    : !isFormValid ? t('validation.fillRequiredFields')
                                    : !hasChanges ? 'No changes to save' : `Save ${entityLabel}`}
                                deleteTitle={deleteDisabled
                                    ? (deleteDisabledTitle || `Cannot delete ${entityLabel}`)
                                    : !hasEntity ? `No ${entityLabel.toLowerCase()} to delete`
                                    : (deleteTitle || `Delete ${entityLabel}`)}
                                isSaving={isSaving}
                            />
                        ) : (
                        <div className={`flex items-center ${toolbarRow ? 'gap-0.5 [&_input]:!h-8 [&_input]:!text-xs [&_button]:!h-8 [&_button]:!p-1 [&_svg]:!h-3.5 [&_svg]:!w-3.5' : dense ? 'gap-0.5 [&_input]:!h-7 [&_input]:!text-xs [&_button]:!h-7 [&_button]:!p-1 [&_svg]:!h-3.5 [&_svg]:!w-3.5' : 'gap-1'} justify-end flex-wrap md:flex-nowrap min-w-0`}>
                            {/* Custom left actions (injected by caller) */}
                            {renderActionsLeft && renderActionsLeft()}

                            {/* Delete button */}
                            {onDelete && (
                                <button
                                    onClick={onDelete}
                                    className={`${actionButtonClass} rounded-lg transition-all duration-200 flex items-center justify-center ${
                                        !hasEntity || isCreating || isSaving || deleteDisabled
                                            ? 'text-gray-400 cursor-not-allowed'
                                            : 'text-red-600 hover:text-red-700 hover:bg-red-50'
                                    }`}
                                    disabled={!hasEntity || isCreating || isSaving || deleteDisabled}
                                    title={
                                        deleteDisabled
                                            ? (deleteDisabledTitle || `Cannot delete ${entityLabel}`)
                                            : !hasEntity
                                            ? `No ${entityLabel.toLowerCase()} to delete`
                                            : (deleteTitle || `Delete ${entityLabel}`)
                                    }
                                >
                                    <Trash size={actionIconSize} />
                                </button>
                            )}

                            {/* Archive button */}
                            {onArchive && (
                                <button
                                    onClick={onArchive}
                                    className={`${actionButtonClass} rounded-lg transition-all duration-200 flex items-center justify-center ${
                                        !hasEntity || isCreating || isSaving
                                            ? 'text-gray-400 cursor-not-allowed'
                                            : 'text-amber-600 hover:text-amber-700 hover:bg-amber-50'
                                    }`}
                                    disabled={!hasEntity || isCreating || isSaving}
                                    title={t('buttons.archive', 'Archive')}
                                >
                                    <Archive size={actionIconSize} />
                                </button>
                            )}

                            {/* Search component (render prop) */}
                            {renderSearch && (
                                <div
                                    ref={searchHighlightRef}
                                    data-header-search-focus="true"
                                    className="rounded-xl transition-all duration-200 ring-0 bg-transparent focus-within:ring-2 focus-within:ring-blue-500 focus-within:bg-blue-50 focus-within:shadow-sm"
                                >
                                    {renderSearch()}
                                </div>
                            )}

                            {/* Save button */}
                            {onSave && (
                                <button
                                    onClick={onSave}
                                    className={`${actionButtonClass} rounded-lg transition-all duration-200 ml-1 flex items-center justify-center ${
                                        isSaving || !hasChanges || !isFormValid || saveDisabled
                                            ? 'text-gray-400 cursor-not-allowed'
                                            : 'text-green-600 hover:text-green-700 hover:bg-green-50'
                                    }`}
                                    disabled={isSaving || !hasChanges || !isFormValid || saveDisabled}
                                    title={
                                        saveDisabled
                                            ? (saveDisabledTitle || `Cannot save ${entityLabel}`)
                                            : !isFormValid
                                            ? t('validation.fillRequiredFields') || 'Please fill all required fields'
                                            : !hasChanges
                                            ? "No changes to save"
                                            : `Save ${entityLabel}`
                                    }
                                >
                                    {isSaving ? <Loader2 size={actionIconSize} className="animate-spin" /> : <Check size={actionIconSize} />}
                                </button>
                            )}

                            {/* Edit button */}
                            {onEdit && (
                                <button
                                    onClick={onEdit}
                                    className={`${actionButtonClass} rounded-lg transition-all duration-200 flex items-center justify-center ${
                                        !hasEntity || isSaving
                                            ? 'text-gray-400 cursor-not-allowed'
                                            : isEditing
                                            ? 'text-orange-600 hover:text-orange-700 hover:bg-orange-50'
                                            : 'text-blue-600 hover:text-blue-700 hover:bg-blue-50'
                                    }`}
                                    disabled={!hasEntity || isSaving}
                                    title={isEditing ? "Cancel Edit" : `Edit ${entityLabel}`}
                                >
                                    <Edit size={actionIconSize} />
                                </button>
                            )}

                            {/* New button */}
                            {onNew && (
                                <button
                                    onClick={onNew}
                                    className={`${actionButtonClass} ${newDisabled ? 'text-gray-400 cursor-not-allowed' : 'text-blue-600 hover:text-blue-700 hover:bg-blue-50'} rounded-lg disabled:cursor-not-allowed transition-all duration-200 flex items-center justify-center`}
                                    disabled={isCreating || isSaving || newDisabled}
                                    title={newDisabled ? (newDisabledTitle || `Cannot create ${entityLabel}`) : (newTitle || `New ${entityLabel}`)}
                                >
                                    <FileText size={actionIconSize} />
                                </button>
                            )}

                            {/* Copy button */}
                            {onCopy && (
                                <button
                                    onClick={onCopy}
                                    className={`${actionButtonClass} rounded-lg transition-all duration-200 flex items-center justify-center ${
                                        !hasEntity || isSaving
                                            ? 'text-gray-400 cursor-not-allowed'
                                            : 'text-purple-600 hover:text-purple-700 hover:bg-purple-50'
                                    }`}
                                    disabled={!hasEntity || isSaving}
                                    title={!hasEntity ? `No ${entityLabel.toLowerCase()} to copy` : (copyTitle || `Copy ${entityLabel}`)}
                                >
                                    <Copy size={actionIconSize} />
                                </button>
                            )}

                            {/* Excel Upload button */}
                            {onExcelUpload && (
                                <button
                                    onClick={onExcelUpload}
                                    className={`${actionButtonClass} ${isSaving || excelUploadDisabled ? 'text-gray-400 cursor-not-allowed' : 'text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50'} rounded-lg transition-all duration-200 flex items-center justify-center`}
                                    disabled={isSaving || excelUploadDisabled}
                                    title={excelUploadDisabled ? (excelUploadDisabledTitle || t('buttons.uploadExcel')) : (t('buttons.uploadExcel') || 'Upload Excel')}
                                >
                                    <Upload size={actionIconSize} />
                                </button>
                            )}

                            {/* Excel Export - render prop takes priority over simple button */}
                            {renderExcelExport ? (
                                renderExcelExport()
                            ) : onExcelDownload && (
                                <button
                                    onClick={onExcelDownload}
                                    className={`${actionButtonClass} text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50 rounded-lg transition-all duration-200 flex items-center justify-center`}
                                    disabled={isSaving}
                                    title={t('common.downloadExcel')}
                                >
                                    <Download size={actionIconSize} />
                                </button>
                            )}

                            {/* PDF Print - render prop dropdown, multi-action dropdown, or simple button */}
                            {renderPrintDropdown ? renderPrintDropdown() : (onDirectPrint || onPrintPdf || onPrintLabels) && (() => {
                                const printActions: PrintAction[] = [];
                                if (onDirectPrint) printActions.push({ onClick: onDirectPrint, icon: <FileDown size={actionIconSize} />, labelKey: 'buttons.directPrint' });
                                if (onPrintPdf) printActions.push({ onClick: onPrintPdf, icon: <SlidersHorizontal size={actionIconSize} />, labelKey: 'buttons.printWithFilters' });
                                if (onPrintLabels) printActions.push({ onClick: onPrintLabels, icon: <Tag size={actionIconSize} />, labelKey: 'buttons.printAsnLabels' });

                                return printActions.length > 1 ? (
                                    <PrintDropdown
                                        actions={printActions}
                                        disabled={isSaving}
                                        t={t}
                                        dense={dense}
                                    />
                                ) : (
                                    <button
                                        onClick={printActions[0]?.onClick}
                                        className={`${actionButtonClass} text-blue-600 hover:text-blue-700 hover:bg-blue-50 rounded-lg transition-all duration-200 flex items-center justify-center`}
                                        disabled={isSaving}
                                        title={t('buttons.printPdf', 'Print PDF')}
                                    >
                                        <Printer size={actionIconSize} />
                                    </button>
                                );
                            })()}
                        </div>
                        )}
                    </div>
                )}
            </div>
            {belowHeader && <div className="w-full mt-3">{belowHeader}</div>}
        </div>
        </>
    );
};

// Print action type for dropdown
interface PrintAction {
    onClick: () => void;
    icon: React.ReactNode;
    labelKey: string;
}

// Print dropdown with dynamic actions
const PrintDropdown: React.FC<{
    actions: PrintAction[];
    disabled: boolean;
    t: (key: string, fallback?: string) => string;
    dense?: boolean;
}> = ({ actions, disabled, t, dense = true }) => {
    const [open, setOpen] = useState(false);
    const dropdownRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const handleClickOutside = (e: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
                setOpen(false);
            }
        };
        if (open) document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [open]);

    return (
        <div className="relative" ref={dropdownRef}>
            <button
                type="button"
                onClick={() => setOpen((prev) => !prev)}
                className={`${dense ? 'p-1 h-7' : 'p-1.5 h-9'} text-blue-600 hover:text-blue-700 hover:bg-blue-50 rounded-lg transition-all duration-200 flex items-center justify-center gap-0.5`}
                disabled={disabled}
                title={t('buttons.printPdf', 'Print PDF')}
            >
                <Printer size={dense ? 14 : 20} />
                <ChevronDown size={dense ? 12 : 14} />
            </button>
            {open && (
                <div className="absolute right-0 top-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-50 min-w-[180px] py-1">
                    {actions.map((action, idx) => (
                        <button
                            key={idx}
                            type="button"
                            onClick={() => { setOpen(false); action.onClick(); }}
                            className="w-full px-3 py-2 text-left text-sm text-gray-700 hover:bg-blue-50 hover:text-blue-700 flex items-center gap-2 transition-colors"
                        >
                            {action.icon}
                            {t(action.labelKey)}
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
};

export default GenericMasterHeader;

